/**
 * Shared helpers for AI report-card narrative drafts
 * (students/{id}/narrativeDrafts).
 *
 * Single-sourced so the resume banner in ReportCardNarrativeModal and the
 * "Saved Report Cards" overview on the Results page resolve a report period to
 * the SAME draft. If the sort/match logic ever diverged, the overview could show
 * one draft's status but "View" could open a different one — these helpers exist
 * specifically to prevent that.
 */

import { schoolYearStartYear } from './schoolYear';

/** Report-type selector options (value + human label). */
export const REPORT_TYPES = [
  { value: 'quarterly', label: 'Quarterly' },
  { value: 'semiannual', label: 'Semi-annual' },
  { value: 'yearend', label: 'Year-end' },
];

export const QUARTERS = [1, 2, 3, 4];

export const SEMESTERS = [
  { value: 'fall', label: 'Fall (Aug–Jan)' },
  { value: 'spring', label: 'Spring (Feb–Jul)' },
];

/** value -> short label, for table cells (no parenthetical month ranges). */
export const REPORT_TYPE_LABELS = {
  quarterly: 'Quarterly',
  semiannual: 'Semi-annual',
  yearend: 'Year-end',
};

/**
 * Current school year as "YYYY-YYYY". Rolls over on July 1 — the canonical
 * school-year boundary (see src/utils/schoolYear.js), aligned in v3.28.0
 * (previously rolled over in August).
 */
export function currentSchoolYear(now = new Date()) {
  const start = schoolYearStartYear(now);
  return `${start}-${start + 1}`;
}

/** This year + the two prior, newest first. */
export function schoolYearOptions() {
  const current = currentSchoolYear();
  const startYear = Number(current.split('-')[0]);
  return [0, 1, 2].map((back) => `${startYear - back}-${startYear - back + 1}`);
}

/** Best-effort millis from a Firestore Timestamp / Date / null for sorting. */
export function millisFromTimestamp(ts) {
  if (!ts) return 0;
  if (typeof ts.toMillis === 'function') return ts.toMillis();
  if (typeof ts.seconds === 'number') return ts.seconds * 1000;
  const d = new Date(ts);
  return Number.isNaN(d.getTime()) ? 0 : d.getTime();
}

/** Sort drafts newest-first by generatedAt. Returns a new array. */
export function sortDraftsByGeneratedDesc(drafts) {
  return [...(drafts || [])].sort(
    (a, b) => millisFromTimestamp(b.generatedAt) - millisFromTimestamp(a.generatedAt),
  );
}

/**
 * The most-recent draft matching a report period, or undefined.
 *
 * Period identity mirrors what the server writes: reportType + schoolYear, plus
 * quarterNumber (quarterly) or semester (semiannual); year-end matches on type +
 * year alone. `drafts` MUST be sorted newest-first (sortDraftsByGeneratedDesc)
 * for "most-recent" to hold, since this returns the first match.
 */
export function findMatchingDraft(drafts, { reportType, schoolYear, quarterNumber, semester }) {
  return (drafts || []).find((d) => {
    if (d.reportType !== reportType || d.schoolYear !== schoolYear) return false;
    if (reportType === 'quarterly') return d.quarterNumber === quarterNumber;
    if (reportType === 'semiannual') return d.semester === semester;
    return true;
  });
}

/** Human label for a report period, for table cells / print headers. */
export function formatPeriodLabel({ reportType, quarterNumber, semester }) {
  if (reportType === 'quarterly') return `Quarter ${quarterNumber}`;
  if (reportType === 'semiannual') return semester === 'spring' ? 'Spring' : 'Fall';
  return 'Year-end';
}
